import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Form } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Dollars from '../../common/typeFormatting/Dollars';
import { getVoucherDetail, getVoucherTransactions, adjustVoucher } from './api/VoucherApi';

const schema = yup.object({
  amount: yup.number().required('Amount is required'),
  description: yup.string().required('Description is required').max(500, 'Description must be less than 500 characters'),
  notes: yup.string().max(500, 'Notes must be less than 500 characters'),
  tranDate: yup.date().required('Transaction date is required')
}).required();

export default function VoucherDetailPage() {
  const [voucher, setVoucher] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();
  const { voucherGroupId, voucherId } = useParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      tranDate: new Date().toISOString().split('T')[0]
    }
  });

  const fetchTransactions = async () => {
    try {
      const transData = await getVoucherTransactions(voucherId);
      setTransactions(transData);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const voucherData = await getVoucherDetail(voucherId);
        setVoucher(voucherData);
        await fetchTransactions();
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error);
        setLoading(false);
      }
    };

    fetchData();
  }, [voucherId]);

  const handleAdjustment = async (data) => {
    try {
      await adjustVoucher(voucherId, data);
      // Refresh voucher details and transactions after successful adjustment
      const updatedVoucher = await getVoucherDetail(voucherId);
      setVoucher(updatedVoucher);
      await fetchTransactions();
      reset(); // Reset form
    } catch (error) {
      console.error('Error applying adjustment:', error);
      setError(error);
    }
  };

  const handleEdit = () => {
    history.push(`/travel/vouchergroups/${voucherGroupId}/vouchers/${voucherId}/edit`);
  };

  const handleBack = () => {
    history.push(`/travel/vouchergroups/${voucherGroupId}`);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="p-4">
      {/* Voucher Details Section */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Card.Title>Allowance Details</Card.Title>
            <div>
              <Button variant="primary" onClick={handleEdit} className="me-2">
                Edit
              </Button>
              <Button variant="secondary" onClick={handleBack}>
                Back
              </Button>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <dl className="row">
                <dt className="col-sm-4">Allowance Name</dt>
                <dd className="col-sm-8">{voucher.voucherName}</dd>
                
                <dt className="col-sm-4">Employee</dt>
                <dd className="col-sm-8">{voucher.employeeName}</dd>
                
                <dt className="col-sm-4">Issue Date</dt>
                <dd className="col-sm-8">{new Date(voucher.issueDate).toLocaleDateString()}</dd>
              </dl>
            </div>
            <div className="col-md-6">
              <dl className="row">
                <dt className="col-sm-4">Initial Amount</dt>
                <dd className="col-sm-8"><Dollars>{voucher.initialAmount}</Dollars></dd>
                
                <dt className="col-sm-4">Current Balance</dt>
                <dd className="col-sm-8"><Dollars>{voucher.balance}</Dollars></dd>
                
                <dt className="col-sm-4">Status</dt>
                <dd className="col-sm-8">{voucher.status}</dd>
              </dl>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Adjustment Form */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Balance Adjustment</Card.Title>
          <Form onSubmit={handleSubmit(handleAdjustment)}>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    {...register('amount')}
                    isInvalid={!!errors.amount}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amount?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    {...register('tranDate')}
                    isInvalid={!!errors.tranDate}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.tranDate?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('description')}
                    isInvalid={!!errors.description}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-12">
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    {...register('notes')}
                    isInvalid={!!errors.notes}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.notes?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
            <Button 
              type="submit" 
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Applying...' : 'Apply Adjustment'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Transactions Table */}
      <Card>
        <Card.Body>
          <Card.Title>Transaction History</Card.Title>
          <Table hover responsive>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th className="text-right">Amount</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(trans => (
                <tr key={trans.voucherTranId}>
                  <td>{new Date(trans.tranDate).toLocaleDateString()}</td>
                  <td>{trans.tranType}</td>
                  <td>{trans.description}</td>
                  <td className="text-right"><Dollars>{trans.amount}</Dollars></td>
                  <td>{trans.notes}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
}